/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import LoginPage from '../Members/LoginPage';
import { User } from 'firebase/auth';

const EXCLUDED_KEYS = new Set(['formId', '_docId']);

const CELL_BORDER = '1px solid #d0d0d0';

const DATE_PATTERNS = /date|time|At$|On$/i;
const NAME_PATTERNS =
  /^(first|last|full)?name$|firstName|lastName|participantName/i;

const toMillis = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'object' && value.toDate)
    return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(String(value));
  return isNaN(parsed) ? 0 : parsed;
};

const sortByDate = (a: any, b: any): number => {
  const dateKey = Object.keys(a).find(
    (k) => DATE_PATTERNS.test(k) && toMillis(a[k])
  );
  if (!dateKey) return 0;
  return toMillis(b[dateKey]) - toMillis(a[dateKey]); // newest first
};

const columnPriority = (key: string): number => {
  // if (DATE_PATTERNS.test(key)) return 0;
  if (key === 'ParticipantName') return 0;
  if (NAME_PATTERNS.test(key)) return 0;
  if (key.toLowerCase().includes('name')) return 0;
  return 1;
};

const sortColumns = (keys: string[]): string[] =>
  [...keys].sort((a, b) => columnPriority(a) - columnPriority(b));

const formatColumnName = (key: string): string => {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
};

const isPlainObject = (v: any): boolean =>
  v !== null && typeof v === 'object' && !v.toDate && !(v instanceof Date);

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object' && value.toDate)
    return value.toDate().toLocaleDateString();
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const SubTable = ({ data }: { data: Record<string, any> }) => {
  const entries = Object.entries(data).filter(([, v]) => v !== undefined);
  return (
    <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 160 }}>
      <TableBody>
        {entries.map(([k, v]) => (
          <TableRow key={k}>
            <TableCell
              sx={{
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                py: 0.25,
                px: 1,
                border: CELL_BORDER,
              }}
            >
              {formatColumnName(k)}
            </TableCell>
            <TableCell sx={{ py: 0.25, px: 1, border: CELL_BORDER }}>
              <CellValue value={v} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const CellValue = ({ value }: { value: any }): React.ReactElement => {
  if (Array.isArray(value)) {
    if (value.length === 0) return <></>;
    if (value.every((item) => isPlainObject(item))) {
      return (
        <>
          {value.map((item, i) => (
            <SubTable key={i} data={item} />
          ))}
        </>
      );
    }
    return <>{value.map((v) => formatValue(v)).join(', ')}</>;
  }
  if (isPlainObject(value)) {
    return <SubTable data={value} />;
  }
  return <>{formatValue(value)}</>;
};

type SortDir = 'asc' | 'desc';

const SubmittedFormTable = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null | undefined>();
  const [busy, setBusy] = useState(false);
  const [forms, setForms] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [filterText, setFilterText] = useState('');
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [colPickerAnchor, setColPickerAnchor] =
    useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!id || !user) return;

    const q = query(collection(db, 'forms'), where('formId', '==', id));
    setBusy(true);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setBusy(false);
      const rows: any[] = [];
      snapshot.forEach((doc) => {
        rows.push({ ...doc.data(), _docId: doc.id });
      });

      rows.sort(sortByDate);
      const keySet = new Set<string>();
      rows.forEach((row) => {
        Object.keys(row).forEach((k) => {
          if (!EXCLUDED_KEYS.has(k)) keySet.add(k);
        });
      });

      setForms(rows);
      setColumns(sortColumns(Array.from(keySet)));
    });

    return () => unsubscribe();
  }, [id, user]);

  const visibleColumns = columns.filter((c) => !hiddenCols.has(c));

  const toggleCol = (col: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      next.has(col) ? next.delete(col) : next.add(col);
      return next;
    });
  };

  const exportToCsv = () => {
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const header = visibleColumns
      .map((c) => escape(formatColumnName(c)))
      .join(',');
    const rows = visibleRows.map((row) =>
      visibleColumns.map((col) => escape(formatValue(row[col]))).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${id}-submissions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSortClick = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const visibleRows = useMemo(() => {
    const needle = filterText.toLowerCase();
    const filtered = needle
      ? forms.filter((row) =>
          columns.some((col) =>
            formatValue(row[col]).toLowerCase().includes(needle)
          )
        )
      : forms;

    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const av = formatValue(a[sortCol]);
      const bv = formatValue(b[sortCol]);
      const cmp = av.localeCompare(bv, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [forms, columns, filterText, sortCol, sortDir]);

  if (!user) return <LoginPage onUserSet={setUser} />;
  if (busy) return <CircularProgress />;

  return (
    <Container sx={{ py: 3 }}>
      <Typography variant="h5" gutterBottom>
        Submitted Forms: <b>{id}</b> ({visibleRows.length}
        {visibleRows.length !== forms.length ? ` of ${forms.length}` : ''})
      </Typography>
      {forms.length === 0 ? (
        <Typography>No submissions found.</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Filter rows…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<ViewColumnIcon />}
              onClick={(e) => setColPickerAnchor(e.currentTarget)}
            >
              Columns
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={exportToCsv}
            >
              Export to Google Sheets
            </Button>
          </Stack>

          <Popover
            open={Boolean(colPickerAnchor)}
            anchorEl={colPickerAnchor}
            onClose={() => setColPickerAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Stack
              sx={{ p: 2, maxHeight: 400, overflowY: 'auto', minWidth: 200 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Toggle columns
              </Typography>
              {columns.map((col) => (
                <FormControlLabel
                  key={col}
                  label={formatColumnName(col)}
                  control={
                    <Checkbox
                      size="small"
                      checked={!hiddenCols.has(col)}
                      onChange={() => toggleCol(col)}
                    />
                  }
                />
              ))}
            </Stack>
          </Popover>
          <TableContainer
            component={Paper}
            sx={{
              overflowX: 'scroll',
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-track': { bgcolor: '#f0f0f0' },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: '#bbb',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#888' },
            }}
          >
            <Table size="small" sx={{ borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        border: CELL_BORDER,
                        bgcolor: '#f5f5f5',
                      }}
                      sortDirection={sortCol === col ? sortDir : false}
                    >
                      <TableSortLabel
                        active={sortCol === col}
                        direction={sortCol === col ? sortDir : 'asc'}
                        onClick={() => handleSortClick(col)}
                      >
                        {formatColumnName(col)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, i) => (
                  <TableRow key={row._docId ?? i} hover>
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          maxWidth: 220,
                          wordBreak: 'break-word',
                          border: CELL_BORDER,
                        }}
                      >
                        <CellValue value={row[col]} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default SubmittedFormTable;
